
import type { PropType } from 'vue'
import type { DateFilter, FiltersMessages } from '../../models'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import { FilterIdEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { compileMessage } from '../../utils/messages.utils'
import { UiMenu, UiPopover } from '../keen-ui'
import { EpbBaseInput } from '../shared/epb-base-input'
import { EpbButton } from '../shared/epb-button'
import CustomDateSelector from './custom-date-selector.component.vue'

export interface UiMenuOption {
  id: 'today' | 'lastSevenDays' | 'lastThirtyDays' | 'custom'
  label: string
  icon?: string
}

export default defineComponent({
  name: 'DateFilterSelector',
  components: {
    CustomDateSelector,
    EpbBaseInput,
    EpbButton,
    UiMenu,
    UiPopover,
  },
  props: {
    filter: {
      type: Object as PropType<DateFilter>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const baseClass = 'epb-date-filter-selector'

    const filtersMessages = ctx.proxy.$epb.messages.filters as FiltersMessages
    const messages = {
      name: filtersMessages.names[FilterIdEnum.DATE],
      values: filtersMessages.values[FilterIdEnum.DATE],
    }

    const options: Array<UiMenuOption | { type: 'divider' }> = [
      {
        id: 'today',
        ...messages.values.today,
      },
      {
        id: 'lastSevenDays',
        ...messages.values.lastSevenDays,
      },
      {
        id: 'lastThirtyDays',
        ...messages.values.lastThirtyDays,
      },
      {
        type: 'divider',
      },
      {
        id: 'custom',
        ...messages.values.custom,
        icon: 'calendar',
      },
    ]

    const customSelector = ref()

    const selectedOption = computed<UiMenuOption | undefined>(() => {
      let selectedOptionId: UiMenuOption['id'] | undefined
      let from: Date, to: Date
      const {
        selectedValues: [fromInMillis, toInMillis],
      } = props.filter
      if (fromInMillis) {
        from = new Date(fromInMillis)
      }
      if (toInMillis) {
        to = new Date(toInMillis)
        to.setUTCDate(to.getUTCDate() - 1)
      }
      if (from && to) {
        const today = getDateFromToday(0)
        if (from.getTime() === today.getTime() && to.getTime() === today.getTime()) {
          selectedOptionId = 'today'
        } else {
          const yesterday = getDateFromToday(-1)
          if (
            from.getTime() === getDateFromToday(-7).getTime() &&
            to.getTime() === yesterday.getTime()
          ) {
            selectedOptionId = 'lastSevenDays'
          } else if (
            from.getTime() === getDateFromToday(-30).getTime() &&
            to.getTime() === yesterday.getTime()
          ) {
            selectedOptionId = 'lastThirtyDays'
          } else {
            selectedOptionId = 'custom'
          }
        }
      } else if (from || to) {
        selectedOptionId = 'custom'
      } else {
        selectedOptionId = undefined
      }
      return selectedOptionId
        ? (options.find(option => (option as UiMenuOption).id === selectedOptionId) as UiMenuOption)
        : undefined
    })

    const selectedValuesText = computed<string>(() => {
      let result: string
      let fromText: string, toText: string
      const {
        selectedValues: [fromInMillis, toInMillis],
      } = props.filter
      const { locale } = ctx.proxy.$epb
      const dateFormatOptions = {
        year: '2-digit',
        month: 'short',
        day: '2-digit',
      } as Intl.DateTimeFormatOptions
      if (fromInMillis) {
        const from = new Date(fromInMillis)
        from.setUTCHours(12)
        fromText = from.toLocaleString(locale, dateFormatOptions)
      }
      if (toInMillis) {
        const to = new Date(toInMillis)
        to.setUTCHours(12)
        to.setUTCDate(to.getUTCDate() - 1)
        toText = to.toLocaleString(locale, dateFormatOptions)
      }
      if (fromText && toText) {
        if (fromText === toText) {
          result = fromText
        } else {
          result = `${fromText} - ${toText}`
        }
      } else if (fromText) {
        result = capitalizeSentence(compileMessage(messages.values.from, { date: fromText }))
      } else if (toText) {
        result = capitalizeSentence(compileMessage(messages.values.to, { date: toText }))
      } else {
        result = ''
      }
      return result
    })

    function selectOption(option: UiMenuOption): void {
      let from: Date, to: Date
      switch (option.id) {
        case 'today':
          from = getDateFromToday(0)
          to = getDateFromToday(0)
          break
        case 'lastSevenDays':
          from = getDateFromToday(-7)
          to = getDateFromToday(-1)
          break
        case 'lastThirtyDays':
          from = getDateFromToday(-30)
          to = getDateFromToday(-1)
          break
        default:
          customSelector.value.open()
          return
      }
      if (to) {
        to.setUTCDate(to.getUTCDate() + 1)
      }
      emit('selectedValuesChange', [from?.getTime(), to?.getTime()])
    }

    function getDateFromToday(days: number): Date {
      const now = new Date(Date.now())
      return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days))
    }

    return {
      baseClass,
      messages,
      options,
      customSelector,
      selectedOption,
      selectedValuesText,
      selectOption,
    }
  },
})
